import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import FieldLabel from "components/common/FieldLabel";
import TypeToConfirmDialog from "components/common/TypeToConfirmDialog";
import FormFooter, {
  FOOTER_BUTTON_SX,
} from "components/userManagement/form/FormFooter";
import FormHeader from "components/userManagement/form/FormHeader";
import { pushNotification } from "features/notificationSlice";
import {
  type SecureCollectionRow,
  useCreateSecureCollectionMutation,
  useDeleteSecureCollectionMutation,
  useRenameSecureCollectionMutation,
} from "features/secureUploads/secureUploads";
import { formatApiError } from "utils/formatApiError";

import { CollectionIcon } from "./icons";
import { countOf } from "./projectModel";

export const COLLECTION_NAME_MAX = 50;

/** What a project page asked to do with a collection. */
export type CollectionAction =
  | { kind: "create"; projectId: number }
  | { kind: "rename" | "delete"; collection: SecureCollectionRow };

function nameError(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const value = (data as Record<string, unknown>).name;
  if (!value) return null;
  return Array.isArray(value) ? value.join(" ") : String(value);
}

function CollectionFormDialog({
  action,
  onClose,
}: {
  action: CollectionAction | null;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const open = action?.kind === "create" || action?.kind === "rename";
  const renaming = action?.kind === "rename";
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [create, { isLoading: creating }] = useCreateSecureCollectionMutation();
  const [rename, { isLoading: renamingNow }] =
    useRenameSecureCollectionMutation();
  const busy = creating || renamingNow;

  useEffect(() => {
    if (!open) return;
    setName(action?.kind === "rename" ? action.collection.name : "");
    setError(null);
  }, [open, action]);

  if (!open || !action) return null;
  const trimmed = name.trim();

  const save = async () => {
    if (!trimmed) {
      setError("Enter a collection name.");
      return;
    }
    try {
      if (action.kind === "rename")
        await rename({ id: action.collection.id, name: trimmed }).unwrap();
      else if (action.kind === "create")
        await create({ projectId: action.projectId, name: trimmed }).unwrap();
      dispatch(
        pushNotification({
          message: renaming ? "Collection renamed." : "Collection created.",
          severity: "success",
        }),
      );
      onClose();
    } catch (failure) {
      const data = (failure as { data?: unknown })?.data;
      const field = nameError(data);
      if (field) setError(field);
      else
        dispatch(
          pushNotification({
            message: formatApiError(data, "The collection could not be saved."),
            severity: "error",
          }),
        );
    }
  };

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <FormHeader
          avatar={<CollectionIcon fontSize="small" />}
          title={renaming ? "Rename Collection" : "New Collection"}
          onClose={onClose}
          closeDisabled={busy}
        />
        <Box sx={{ p: 3 }}>
          <FieldLabel required>Collection Name</FieldLabel>
          <TextField
            fullWidth
            autoFocus
            size="small"
            placeholder="Enter a name for your collection"
            value={name}
            disabled={busy}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") void save();
            }}
            inputProps={{ maxLength: COLLECTION_NAME_MAX }}
            error={Boolean(error)}
            helperText={error ?? `${trimmed.length}/${COLLECTION_NAME_MAX}`}
          />
        </Box>
      </DialogContent>
      <FormFooter
        mode="edit"
        isFirstStep
        onBack={() => undefined}
        onCancel={onClose}
      >
        <Button
          variant="contained"
          size="small"
          onClick={save}
          disabled={busy || !trimmed}
          sx={FOOTER_BUTTON_SX}
        >
          Save
        </Button>
      </FormFooter>
    </Dialog>
  );
}

/** A collection with files asks for its name: they all go to Deleted Files. */
function DeleteCollectionDialog({
  action,
  onClose,
  onDeleted,
}: {
  action: CollectionAction | null;
  onClose: () => void;
  onDeleted?: (collection: SecureCollectionRow) => void;
}) {
  const dispatch = useDispatch();
  const [remove, { isLoading, error, reset }] =
    useDeleteSecureCollectionMutation();
  const collection = action?.kind === "delete" ? action.collection : null;
  useEffect(() => {
    if (collection) reset();
  }, [collection, reset]);
  if (!collection) return null;
  const hasFiles = collection.files > 0;
  const confirm = async () => {
    try {
      await remove(collection.id).unwrap();
      dispatch(
        pushNotification({
          message: "Collection deleted.",
          severity: "success",
        }),
      );
      onDeleted?.(collection);
      onClose();
    } catch {
      // The reason shows in the dialog.
    }
  };
  return (
    <TypeToConfirmDialog
      open
      title="Delete Collection"
      subject={collection.name}
      confirmLabel="Delete Collection"
      confirmPhrase={hasFiles ? collection.name : null}
      phraseLabel="Type the collection name to confirm"
      phrasePlaceholder={collection.name}
      busy={isLoading}
      error={
        error
          ? formatApiError(
              (error as { data?: unknown })?.data,
              "The collection could not be deleted.",
            )
          : null
      }
      onCancel={onClose}
      onConfirm={confirm}
    >
      {hasFiles ? (
        <>
          <Typography variant="body2">
            Deleting the collection will remove all{" "}
            <b>{countOf(collection.files, "file", "files")}</b> from everyone
            who has access, including client users.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Files will be retained for <b>60 days.</b>
          </Typography>
        </>
      ) : (
        <Typography variant="body2">
          Deleting the collection will remove it from everyone who has access,
          including client users.
        </Typography>
      )}
    </TypeToConfirmDialog>
  );
}

/** Every collection dialog behind one switch, for the project page. */
export default function CollectionDialogs({
  action,
  onClose,
}: {
  action: CollectionAction | null;
  onClose: () => void;
}) {
  return (
    <>
      <CollectionFormDialog action={action} onClose={onClose} />
      <DeleteCollectionDialog action={action} onClose={onClose} />
    </>
  );
}
